import * as React from 'react';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { Kunde, useKundeList } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { cloneDeep, isEqual } from 'lodash';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import {
    Ansprechpartner,
    addAnsprechpartner,
    defaultAnsprechpartner,
    updateAnsprechpartner,
    useAnsprechpartnerList,
} from 'api/ansprechpartner';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { BetreuerByKz } from 'formComponents/lookups/BetreuerByKz';
import { Anrede } from 'formComponents/lookups/Anrede';
import { Timestamps } from 'formComponents/Timestamps';
import { Sprache } from 'formComponents/lookups/Sprache';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { logDifference } from 'utils/helper';

const styles = mergeStyleSets({
    nameLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 3fr',
        gridColumnGap: 10,
    },
    lastLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '3fr 1fr',
        gridColumnGap: 10,
    },
});

interface AnsprechpartnerEditProps {
    kNr: number;
    kaNr: number; // -1 = new
    onClose: (refetch: boolean) => void;
}

export const AnsprechpartnerEdit: React.FC<AnsprechpartnerEditProps> = ({ kNr, kaNr, onClose }) => {
    const auth = useSelector(authSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const isMounted = useIsMounted();

    const ansprechpartnerList = useAnsprechpartnerList(
        {
            kNr,
            nr: kaNr,
            includeArchiv: true,
        },
        true,
        false,
    );

    const [ansprechpartner, setAnsprechpartner] = React.useState<Ansprechpartner | null>(null);

    const kundeList = useKundeList({
        nr: kNr,
        includeArchiv: true,
    });

    const [kunde, setKunde] = React.useState<Kunde | null>(null);

    React.useEffect(() => {
        if (!kundeList.isLoading && !kundeList.isFetching && kundeList.data?.length === 1 && kunde === null) {
            setKunde(kundeList.data[0]);
        }
    }, [kunde, kundeList]);

    React.useEffect(() => {
        if (!ansprechpartnerList.isLoading && !ansprechpartnerList.isFetching && ansprechpartner === null) {
            if (ansprechpartnerList.data?.length === 1) {
                setAnsprechpartner(cloneDeep(ansprechpartnerList.data[0]));
                setSavingState(SavingState.notChanged);
            } else {
                setAnsprechpartner({ ...defaultAnsprechpartner, nr: -1, kNr });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [ansprechpartner, ansprechpartnerList, kNr, auth]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !ansprechpartnerList.isLoading &&
            !ansprechpartnerList.isFetching &&
            ansprechpartnerList.data?.length === 1 &&
            ansprechpartner !== null &&
            !isEqual(ansprechpartnerList.data[0], ansprechpartner)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('ansprechpartner', ansprechpartner, ansprechpartnerList.data[0]);
        }
    }, [savingState, ansprechpartnerList, ansprechpartner]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const save = () => {
        if (ansprechpartner && savingState !== SavingState.saving) {
            setSavingState(SavingState.saving);
            if (ansprechpartner.nr === -1) {
                addAnsprechpartner(ansprechpartner)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            setSavingState(SavingState.notChanged);
                        }
                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            } else {
                updateAnsprechpartner(ansprechpartner)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            setSavingState(SavingState.notChanged);
                        }
                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            }
        }
    };

    const copy = () => {
        if (ansprechpartner) {
            setAnsprechpartner({ ...ansprechpartner, nr: -1 });
        }
    };

    const renderCheckbox = (column: keyof Ansprechpartner, label: string) => (
        <Checkbox
            className={formStyles.checkbox}
            label={label}
            disabled={savingState === SavingState.saving}
            checked={ansprechpartner?.[column] as boolean}
            onChange={(event, checked) => {
                if (ansprechpartner) {
                    setAnsprechpartner({
                        ...ansprechpartner,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    const renderTextField = (column: keyof Ansprechpartner, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(ansprechpartner?.[column] || '') as string}
            savingState={savingState}
            options={options}
            onChange={(newValue): void => {
                if (ansprechpartner) {
                    setAnsprechpartner({
                        ...ansprechpartner,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const generateBriefanrede = (newAnsprechpartner: Ansprechpartner): string => {
        let briefanrede: string;
        const nachname = newAnsprechpartner.name.substring(newAnsprechpartner.name.indexOf(' ') || 0).trim();
        switch (newAnsprechpartner.anrede) {
            case 'F':
                briefanrede =
                    'Sehr geehrte Frau ' +
                    (newAnsprechpartner.titel ? newAnsprechpartner.titel + ' ' : '') +
                    nachname +
                    '!';
                break;
            case 'H':
                briefanrede =
                    'Sehr geehrter Herr ' +
                    (newAnsprechpartner.titel ? newAnsprechpartner.titel + ' ' : '') +
                    nachname +
                    '!';
                break;
            default:
                briefanrede = 'Sehr geehrte Damen und Herren!';
        }
        return briefanrede;
    };

    const renderTextFieldWithBriefanrede = (
        column: keyof Ansprechpartner,
        label: string,
        options: FormTextFieldOptions = {},
    ) => (
        <FormTextField
            value={(ansprechpartner?.[column] || '') as string}
            savingState={savingState}
            options={options}
            onChange={(newValue): void => {
                if (ansprechpartner) {
                    const newAnsprechpartner = {
                        ...ansprechpartner,
                        [column]: newValue,
                    };

                    setAnsprechpartner({
                        ...newAnsprechpartner,
                        briefanrede: generateBriefanrede(newAnsprechpartner),
                    });
                }
            }}
            label={label}
        />
    );

    const renderAnrede = () => (
        <Anrede
            value={ansprechpartner?.anrede || ''}
            disabled={savingState === SavingState.saving}
            onChange={(newValue): void => {
                if (ansprechpartner) {
                    const newAnsprechpartner = {
                        ...ansprechpartner,
                        anrede: newValue,
                    };

                    setAnsprechpartner({
                        ...newAnsprechpartner,
                        briefanrede: generateBriefanrede(newAnsprechpartner),
                    });
                }
            }}
        />
    );

    const renderGebDat = () => (
        <FormDatePicker
            label="Geburtsdatum"
            savingState={savingState}
            value={ansprechpartner?.gebDat}
            onChange={(newValue) => {
                if (ansprechpartner) {
                    setAnsprechpartner({
                        ...ansprechpartner,
                        gebDat: newValue,
                    });
                }
            }}
        />

        /*
        <DatePicker
            label="Geburtsdatum"
            disabled={savingState === SavingState.saving}
            className={formStyles.datePicker}
            firstDayOfWeek={DayOfWeek.Monday}
            strings={datePickerStrings}
            textField={{
                required: false,
                inputClassName: '',
                onRenderSuffix: () => (
                    <IconButton
                        onClick={() => {
                            if (ansprechpartner) {
                                setAnsprechpartner({
                                    ...ansprechpartner,
                                    gebDat: defaultSmDate,
                                });
                            }
                        }}
                        iconProps={{ iconName: 'Clear', style: { fontSize: 10 } }}
                        style={{ marginRight: 28, width: 15, height: 15 }}
                    />
                ),
                styles: { suffix: { padding: '0 4px' } },
            }}
            value={
                ansprechpartner?.gebDat && ansprechpartner.gebDat !== defaultSmDate
                    ? moment(ansprechpartner?.gebDat).toDate()
                    : undefined
            }
            formatDate={onFormatDate}
            styles={{
                root: {
                    marginBottom: 0,
                },
            }}
            onSelectDate={(value) => {
                if (ansprechpartner) {
                    setAnsprechpartner({
                        ...ansprechpartner,
                        gebDat: value ? moment(value).format('YYYY-MM-DD') + 'T00:00:00' : defaultSmDate,
                    });
                }
            }}
        />
         */
    );

    const renderMa = () => (
        <BetreuerByKz
            value={ansprechpartner?.userKz || ''}
            disabled={savingState === SavingState.saving}
            label="Mitarb. Kürzel"
            menuPlacement="top"
            allowEmpty
            onChange={(newValue): void => {
                if (ansprechpartner) {
                    setAnsprechpartner({
                        ...ansprechpartner,
                        userKz: newValue,
                    });
                }
            }}
        />
    );

    const renderSprache = () => (
        <Sprache
            value={ansprechpartner?.sprache || 'D'}
            disabled={savingState === SavingState.saving}
            menuPlacement="top"
            onChange={(newValue): void => {
                if (ansprechpartner) {
                    setAnsprechpartner({
                        ...ansprechpartner,
                        sprache: newValue,
                    });
                }
            }}
        />
    );

    return (
        <Dialog
            hidden={false}
            title={`Ansprechpartner bei ${kunde?.name1 || '...'} ${kunde?.name2 || ''}`}
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
                dragOptions: {
                    moveMenuItemText: 'Move',
                    closeMenuItemText: 'Close',
                    menu: ContextualMenu,
                },
            }}
            minWidth={800}
            onDismiss={() => {
                onClose(false);
            }}
        >
            {routerConfirmMessage.render()}
            {ansprechpartnerList.isLoading ||
            ansprechpartnerList.isFetching ||
            kundeList.isLoading ||
            kundeList.isFetching ||
            !ansprechpartner ||
            !kunde ? (
                <LoadingIndicator />
            ) : (
                <form className={formStyles.form}>
                    <div className={formStyles.topBlock}>
                        {renderCheckbox('eRechnung', 'Elektronische Rechnung')}
                        {renderCheckbox('archiv', 'Archiv')}
                    </div>

                    <div className={styles.nameLine}>
                        <div className={formStyles.gridCell}>{renderAnrede()}</div>
                        <div className={formStyles.gridCell}>
                            {renderTextFieldWithBriefanrede('titel', 'Titel', { maxLength: 20 })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderTextFieldWithBriefanrede('name', 'Name', { maxLength: 50 })}
                        </div>
                    </div>

                    <div className={formStyles.grid2}>
                        <div className={formStyles.gridCell}>
                            {renderTextField('position', 'Position', { maxLength: 30 })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderTextField('abteilung', 'Abteilung', { maxLength: 30 })}
                        </div>
                    </div>

                    <div className={formStyles.grid2}>
                        <div className={formStyles.gridCell}>
                            {renderTextField('telefon', 'Telefon', { maxLength: 50 })}
                        </div>
                        <div className={formStyles.gridCell}>{renderTextField('fax', 'Fax', { maxLength: 50 })}</div>
                    </div>

                    <div className={formStyles.grid2}>
                        <div className={formStyles.gridCell}>
                            {renderTextField('mobil', 'Mobil', { maxLength: 50 })}
                        </div>

                        <div className={formStyles.gridCell}>
                            {renderTextField('mail', 'Mailadresse', { maxLength: 50 })}
                        </div>
                    </div>

                    <div className={formStyles.grid3}>
                        <div className={formStyles.gridCell}>{renderGebDat()}</div>
                        <div className={formStyles.gridCell}>{renderSprache()}</div>
                        <div className={formStyles.gridCell}>{renderMa()}</div>
                    </div>
                    <div className={formStyles.line}>
                        {renderTextField('briefanrede', 'Briefanrede', { maxLength: 50 })}
                    </div>

                    <Timestamps
                        modifiedDate={ansprechpartner.updDat}
                        modifiedDecimalTime={ansprechpartner.updUhr}
                        modifiedUser={ansprechpartner.updUser}
                        showCreated={false}
                    />

                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}
                    <DialogFooter>
                        <PrimaryButton
                            className={formStyles.copyButton}
                            onClick={copy}
                            text="Kopieren"
                            disabled={savingState !== SavingState.notChanged || ansprechpartnerList.isLoading}
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton onClick={save} text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={save}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || ansprechpartnerList.isLoading}
                            />
                        )}
                    </DialogFooter>
                </form>
            )}
        </Dialog>
    );
};
